// Árvore de filtros do cadastro de disparos: condições, grupos (o
// parêntese) e o combinador E/OU de cada grupo.
//
// É o formato que o backend grava e aceita — `disparo-filtro.helper.ts` tem
// `montarWhereArvore`, `normalizarFiltros` (array legado → GRUPO E) e os
// tetos que este arquivo respeita. Nasceu como proposta no playground do
// Storybook e passou a sustentar o formulário de produção.
//
// O desenho é o que a branch feat/storybook já tinha (FiltroGrupoView +
// filtro-arvore.ts), portado para o modelo atual — condição agora é
// `propriedade · operador · valores[]` do catálogo do backend, não o
// `campo · valor · valorFim` do protótipo antigo.

import {
  CombinadorFiltro,
  FiltroDisparo,
  FiltroGrupoDisparo,
  FiltroNoDisparo,
  MAX_CONDICOES_FILTRO,
  MAX_PROFUNDIDADE_FILTRO,
  OperadorFiltro,
  PropriedadeCatalogo,
  TipoNoFiltro,
  filtroEstaCompleto,
  quantidadeValores,
} from "@/models/consignado/disparo.model";

const novoId = (prefixo: string) => `${prefixo}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Uma condição em edição. O `id` é só da UI — serve para saber qual pill foi
 * clicada e qual popover está aberto, e some na conversão para o DTO.
 */
export interface FiltroDraft {
  id: string;
  propriedade: string;
  operador: OperadorFiltro;
  valores: string[];
}

export const novoFiltro = (): FiltroDraft => ({
  id: novoId("filtro"),
  propriedade: "",
  operador: OperadorFiltro.IGUAL,
  valores: [""],
});

export const COMBINADOR_LABEL: Record<CombinadorFiltro, string> = {
  [CombinadorFiltro.E]: "Todas as condições precisam ser verdadeiras",
  [CombinadorFiltro.OU]: "Basta uma das condições ser verdadeira",
};

/**
 * Uma condição é o mesmo FiltroDraft de hoje — de propósito, para o
 * FiltroCondicaoCard servir aos dois desenhos sem alteração. O que a árvore
 * acrescenta é onde a condição mora, não o que ela é.
 */
export interface CondicaoNo extends FiltroDraft {
  tipoNo: "condicao";
}

export interface GrupoNo {
  tipoNo: "grupo";
  id: string;
  combinador: CombinadorFiltro;
  itens: FiltroNo[];
}

export type FiltroNo = CondicaoNo | GrupoNo;

export const novaCondicao = (): CondicaoNo => ({ ...novoFiltro(), tipoNo: "condicao" });

/** Ajusta a lista de valores ao que o operador novo espera. */
export const valoresParaOperador = (valores: string[], operador: OperadorFiltro): string[] => {
  const esperado = quantidadeValores(operador);
  if (esperado === null) return valores.filter((v) => v.trim() !== "");
  const ajustados = [...valores];
  while (ajustados.length < esperado) ajustados.push("");
  return ajustados.slice(0, esperado);
};

/**
 * Condição que já nasce com o campo escolhido na sidebar — o "+" abre o
 * seletor antes de existir pill, então não há condição vazia no meio do
 * caminho. Operador é o primeiro que a propriedade aceita.
 */
export const condicaoComCampo = (p: PropriedadeCatalogo): CondicaoNo => {
  const operador = p.operadores[0] ?? OperadorFiltro.IGUAL;
  return {
    ...novaCondicao(),
    propriedade: p.id,
    operador,
    valores: valoresParaOperador([], operador),
  };
};

export const novoGrupo = (combinador: CombinadorFiltro = CombinadorFiltro.E): GrupoNo => ({
  tipoNo: "grupo",
  id: novoId("grupo"),
  combinador,
  itens: [],
});

// ---------------------------------------------------------------------------
// Edição imutável — cada função devolve uma raiz nova
//
// Mesma assinatura do filtro-arvore.ts da feat/storybook: "acha o nó pelo id
// em qualquer profundidade" se repete em toda operação, então fica num lugar
// só.
// ---------------------------------------------------------------------------

const mapearItens = (grupo: GrupoNo, transformar: (no: FiltroNo) => FiltroNo): GrupoNo => ({
  ...grupo,
  itens: grupo.itens.map(transformar),
});

export const adicionarCondicao = (
  raiz: GrupoNo,
  grupoAlvoId: string,
  condicao: CondicaoNo = novaCondicao(),
): GrupoNo => {
  if (raiz.id === grupoAlvoId) return { ...raiz, itens: [...raiz.itens, condicao] };
  return mapearItens(raiz, (no) =>
    no.tipoNo === "grupo" ? adicionarCondicao(no, grupoAlvoId, condicao) : no,
  );
};

export const adicionarSubgrupo = (
  raiz: GrupoNo,
  grupoAlvoId: string,
  condicao: CondicaoNo = novaCondicao(),
): GrupoNo => {
  if (raiz.id === grupoAlvoId) {
    // O subgrupo nasce com o combinador oposto ao do pai: é o motivo de
    // existir um parêntese — dentro dele a regra de combinação é outra.
    const oposto =
      raiz.combinador === CombinadorFiltro.E ? CombinadorFiltro.OU : CombinadorFiltro.E;
    return { ...raiz, itens: [...raiz.itens, { ...novoGrupo(oposto), itens: [condicao] }] };
  }
  return mapearItens(raiz, (no) =>
    no.tipoNo === "grupo" ? adicionarSubgrupo(no, grupoAlvoId, condicao) : no,
  );
};

export const atualizarCondicao = (
  raiz: GrupoNo,
  condicaoId: string,
  patch: Partial<FiltroDraft>,
): GrupoNo =>
  mapearItens(raiz, (no) => {
    if (no.tipoNo === "condicao") return no.id === condicaoId ? { ...no, ...patch } : no;
    return atualizarCondicao(no, condicaoId, patch);
  });

export const atualizarCombinador = (
  raiz: GrupoNo,
  grupoAlvoId: string,
  combinador: CombinadorFiltro,
): GrupoNo => {
  if (raiz.id === grupoAlvoId) return { ...raiz, combinador };
  return mapearItens(raiz, (no) =>
    no.tipoNo === "grupo" ? atualizarCombinador(no, grupoAlvoId, combinador) : no,
  );
};

/** Remove condição ou subgrupo (com o conteúdo) pelo id. Nunca remove a raiz. */
export const removerNo = (raiz: GrupoNo, id: string): GrupoNo => ({
  ...raiz,
  itens: raiz.itens
    .filter((no) => no.id !== id)
    .map((no) => (no.tipoNo === "grupo" ? removerNo(no, id) : no)),
});

export const encontrarCondicao = (raiz: GrupoNo, id: string): CondicaoNo | undefined => {
  for (const no of raiz.itens) {
    if (no.tipoNo === "condicao") {
      if (no.id === id) return no;
    } else {
      const achada = encontrarCondicao(no, id);
      if (achada) return achada;
    }
  }
  return undefined;
};

export const condicoesDaArvore = (raiz: GrupoNo): CondicaoNo[] =>
  raiz.itens.flatMap((no) => (no.tipoNo === "condicao" ? [no] : condicoesDaArvore(no)));

/**
 * Profundidade, contada **como o backend conta** (`profundidadeMaxima` em
 * disparo-filtro.helper.ts): a condição também é um nível. Logo, lista plana
 * = 2, um subgrupo = 3.
 *
 * Isso importa porque o teto é 3: cabe um único nível de aninhamento. Contar
 * só os grupos, como esta função fazia antes, faria a tela oferecer
 * aninhamento que o POST recusaria.
 */
export const profundidade = (no: FiltroNo, atual = 1): number => {
  if (no.tipoNo === "condicao") return atual;
  if (!no.itens.length) return atual;
  return Math.max(...no.itens.map((filho) => profundidade(filho, atual + 1)));
};

/** Já está no teto de aninhamento? Então o botão de subgrupo precisa sair. */
export const podeAninhar = (raiz: GrupoNo, grupoAlvoId: string): boolean => {
  const nivel = nivelDoGrupo(raiz, grupoAlvoId);
  // O subgrupo novo nasce com uma condição dentro: nivel + 1 (grupo) + 1
  // (condição) precisa caber no teto.
  return nivel !== null && nivel + 2 <= MAX_PROFUNDIDADE_FILTRO;
};

const nivelDoGrupo = (no: FiltroNo, alvoId: string, atual = 1): number | null => {
  if (no.tipoNo === "condicao") return null;
  if (no.id === alvoId) return atual;
  for (const filho of no.itens) {
    const achado = nivelDoGrupo(filho, alvoId, atual + 1);
    if (achado !== null) return achado;
  }
  return null;
};

/** Teto de condições da árvore inteira, não por grupo. */
export const podeAdicionarCondicao = (raiz: GrupoNo): boolean =>
  condicoesDaArvore(raiz).length < MAX_CONDICOES_FILTRO;

/**
 * Grupos sem nenhuma condição dentro. Importa porque o vazio não é neutro: o
 * backend resolve grupo OU vazio como `FALSE`, então um subgrupo esvaziado
 * derrubaria a busca inteira em silêncio.
 */
export const gruposVazios = (raiz: GrupoNo): GrupoNo[] =>
  raiz.itens.flatMap((no) =>
    no.tipoNo === "grupo" ? [...(no.itens.length === 0 ? [no] : []), ...gruposVazios(no)] : [],
  );

export const arvoreEstaCompleta = (raiz: GrupoNo): boolean =>
  condicoesDaArvore(raiz).every((c) => !!c.propriedade && filtroEstaCompleto(c));

// ---------------------------------------------------------------------------
// O que iria no corpo da requisição
// ---------------------------------------------------------------------------

/**
 * Draft → DTO. Note que o `id` some: ele é da UI, como já acontece no
 * `draftParaDto` do disparo-draft. O que o backend recebe é só a estrutura.
 */
export const paraDtoArvore = (raiz: GrupoNo): FiltroGrupoDisparo => ({
  tipoNo: TipoNoFiltro.GRUPO,
  combinador: raiz.combinador,
  itens: raiz.itens.map((no) =>
    no.tipoNo === "grupo"
      ? paraDtoArvore(no)
      : {
          tipoNo: TipoNoFiltro.CONDICAO as const,
          propriedade: no.propriedade,
          operador: no.operador,
          valores: no.valores.filter((v) => v.trim() !== ""),
        },
  ),
});

/**
 * Compatibilidade com o que já está gravado: uma lista plana é exatamente um
 * grupo raiz com combinador E — é o mesmo que `normalizarFiltros` faz no
 * backend. Nenhum disparo existente precisa ser migrado.
 */
export const arvoreDeListaPlana = (filtros: FiltroDraft[]): GrupoNo => ({
  ...novoGrupo(CombinadorFiltro.E),
  itens: filtros.map((f) => ({ ...f, tipoNo: "condicao" })),
});

/**
 * Resposta da API → árvore editável. O GET devolve a árvore gravada, mas um
 * cadastro anterior à árvore volta como lista plana, e o campo é `unknown` na
 * prática — daí aceitar as duas formas aqui, em vez de deixar a tela quebrar
 * com um `.map` num objeto.
 *
 * Os `id` de UI nascem nesta conversão: o que vem do backend não tem nenhum.
 */
export const arvoreDeFiltrosApi = (filtros: FiltroGrupoDisparo | FiltroDisparo[]): GrupoNo => {
  if (Array.isArray(filtros)) {
    return arvoreDeListaPlana(
      filtros.map((f) => ({ ...novoFiltro(), ...f, valores: [...f.valores] })),
    );
  }
  return grupoDeFiltroApi(filtros);
};

/** Condição da API → condição editável, com o id de UI que ela não tem. */
const condicaoDeFiltroApi = (condicao: FiltroDisparo): CondicaoNo => ({
  ...novoFiltro(),
  propriedade: condicao.propriedade,
  operador: condicao.operador,
  valores: [...(condicao.valores ?? [])],
  tipoNo: "condicao",
});

/**
 * O backend aceita o nó sem `tipoNo` (normalizarFiltros deduz pelo formato),
 * então a leitura deduz do mesmo jeito: quem tem `itens` é grupo.
 */
const ehGrupoApi = (no: FiltroNoDisparo): no is FiltroGrupoDisparo =>
  Array.isArray((no as FiltroGrupoDisparo)?.itens);

const grupoDeFiltroApi = (no: FiltroNoDisparo | undefined | null): GrupoNo => {
  if (!no) return novoGrupo(CombinadorFiltro.E);
  // Raiz que veio como condição solta: vira um grupo E com ela dentro, que é
  // o que o backend faria ao normalizar.
  if (!ehGrupoApi(no)) {
    return { ...novoGrupo(CombinadorFiltro.E), itens: [condicaoDeFiltroApi(no)] };
  }
  return {
    ...novoGrupo(no.combinador ?? CombinadorFiltro.E),
    itens: no.itens.map((filho) =>
      ehGrupoApi(filho) ? grupoDeFiltroApi(filho) : condicaoDeFiltroApi(filho),
    ),
  };
};

// ---------------------------------------------------------------------------
// Prévia do WHERE
//
// Espelha o montarWhere do disparo-preview.service.ts, com duas diferenças
// que são justamente o assunto da conversa: a recursão e o elemento neutro
// do grupo vazio.
//
// A coluna aparece aqui pelo **id do catálogo** (`status`, `tomador.email`).
// No backend ela vira `prop.sql`, que nunca é exposto ao front — então o
// SQL abaixo é a forma, não o texto final.
// ---------------------------------------------------------------------------

const expressaoDaCondicao = (c: CondicaoNo, params: string[]): string => {
  const col = c.propriedade || "?";
  const valores = c.valores.filter((v) => v.trim() !== "");
  const push = (v: string) => {
    params.push(v);
    return `$${params.length}`;
  };

  switch (c.operador) {
    case OperadorFiltro.IGUAL:
      return `${col} = ${push(valores[0] ?? "")}`;
    case OperadorFiltro.EM:
      return `${col} = ANY(${push(`{${valores.join(",")}}`)})`;
    case OperadorFiltro.MAIOR_OU_IGUAL:
      return `${col} >= ${push(valores[0] ?? "")}`;
    case OperadorFiltro.MENOR_QUE:
      return `${col} < ${push(valores[0] ?? "")}`;
    case OperadorFiltro.ENTRE:
      return `${col} >= ${push(valores[0] ?? "")} AND ${col} < ${push(valores[1] ?? "")}`;
    default:
      return "TRUE";
  }
};

/**
 * O elemento neutro depende do combinador: grupo E vazio é TRUE (como o
 * `partes = ['TRUE']` de hoje), grupo OU vazio é **FALSE**. Se o OU vazio
 * virasse TRUE, um grupo pela metade abriria o público inteiro — o oposto do
 * fail-closed que `Públicos e filtros` exige.
 */
export const montarWhereProposto = (
  raiz: GrupoNo,
  params: string[] = [],
): { sql: string; params: string[] } => {
  const neutro = raiz.combinador === CombinadorFiltro.E ? "TRUE" : "FALSE";
  if (raiz.itens.length === 0) return { sql: neutro, params };

  const partes = raiz.itens.map((no) =>
    no.tipoNo === "condicao"
      ? expressaoDaCondicao(no, params)
      : `(${montarWhereProposto(no, params).sql})`,
  );

  return {
    sql: partes.join(raiz.combinador === CombinadorFiltro.E ? " AND " : " OR "),
    params,
  };
};
